import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import * as css from './userprofile.module.scss';
import PanelHeader from '../components/panelheader/panelHeader';
import translations from './userprofile.i18n';
import { useLoader } from '../api/useLoader';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { dateWithLocale, getStudyYearForDate } from '../date.utils';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import {
  PraksisaktivitetInfoDetaljertPsDto as PsPraksisaktivitetInfoDto,
  PraksisaktivitetInfoDetaljertUhDto as UhPraksisaktivitetInfoDto,
  PraksisaktivitetStatusDto as PsPraksisaktivitetStatusDto,
  StudiestedRollerDto,
  PraksisstedRollerDto,
} from '../api/generated/praksissted-generated-api';

import { Link } from 'react-router-dom';
import statusTranslations from '../commontranslations/psaStatuses.i18n';
import psaRoleTranslations from '../commontranslations/psaRoles.i18n';
import uhaRoleTranslations from '../commontranslations/uhaRoles.i18n';
import { Language } from '../i18n/LanguageStore';
import { EmptyState } from '../components/emptyState/emptyState';
import { useFormatMessageFor } from '../i18n/translation';

interface Props {
  userProfileId: string;
  userName: string;
  isSelf: boolean;
  isPraksisstedBruker: boolean;
}

const Activities: React.FC<React.PropsWithChildren<Props>> = ({
  userProfileId,
  userName,
  isSelf,
  isPraksisstedBruker,
}) => {
  const { brukerApi } = useContext(PraksisstedApiContext);

  const { data: praksisaktiviteter, loading } = useLoader<
    PsPraksisaktivitetInfoDto[] | UhPraksisaktivitetInfoDto[]
  >(
    () =>
      isPraksisstedBruker
        ? brukerApi.praksisaktiviteterDetaljertPsIdGet({
            pathParams: { id: userProfileId },
          })
        : brukerApi.praksisaktiviteterDetaljertUhIdGet({
            pathParams: { id: userProfileId },
          }),
    [userProfileId],
  );

  const [activeCol, setActiveCol] = useState<number>(0);
  const [tableRows, setTableRows] = useState<JSX.Element[]>();

  const studyYear = getStudyYearForDate(new Date(Date.now()));
  const year = `${studyYear[0].getFullYear()}/${studyYear[1].getFullYear()}`;

  const isAktivitetInStudyYear = (
    aktivitet: PsPraksisaktivitetInfoDto | UhPraksisaktivitetInfoDto,
  ) => {
    const startDato = new Date(aktivitet.startDato);
    const sluttDato = new Date(aktivitet.sluttDato);

    return startDato >= studyYear[0] && sluttDato <= studyYear[1];
  };

  const aktiviteterInStudyYear = praksisaktiviteter
    ? (
        praksisaktiviteter as (
          | PsPraksisaktivitetInfoDto
          | UhPraksisaktivitetInfoDto
        )[]
      ).filter(
        (aktivitet: PsPraksisaktivitetInfoDto | UhPraksisaktivitetInfoDto) =>
          isAktivitetInStudyYear(aktivitet),
      )
    : [];

  const { formatMessage, locale } = useIntl();
  const title = formatMessage({ id: translations.activities.id }, { year });
  const subtitle = isSelf
    ? formatMessage(
        {
          id: translations.activititesSubtitleSelf.id,
        },
        {
          name: userName,
          year,
        },
      )
    : formatMessage(
        {
          id: translations.activititesSubtitleUser.id,
        },
        {
          name: userName,
          year,
        },
      );

  const emptyStateTitle = formatMessage(
    {
      id: translations.emptyPracticeActivitiesTitle.id,
    },
    {
      name: userName,
      year,
    },
  );

  const emptyStateText = formatMessage(
    {
      id: translations.emptyPracticeActivitiesText.id,
    },
    {
      name: userName,
      year,
    },
  );

  const statusLabels = useFormatMessageFor(statusTranslations);

  const findStatus = (status: PsPraksisaktivitetStatusDto): string => {
    switch (status) {
      case PsPraksisaktivitetStatusDto.KanBekreftes:
        return statusLabels.kanBekreftes;
      case PsPraksisaktivitetStatusDto.BekreftetMenManglerKapasitet:
        return statusLabels.manglerKapasitet;
      case PsPraksisaktivitetStatusDto.BekreftetOverstyrtKapasitet:
        return statusLabels.overstyrtKapasitet;
      case PsPraksisaktivitetStatusDto.BekreftetOgHarKapasitet:
        return statusLabels.harKapasitet;
      case PsPraksisaktivitetStatusDto.BekreftetMenManglerFordeling:
        return statusLabels.manglerFordeling;
      case PsPraksisaktivitetStatusDto.KlarForGjennomfoering:
        return statusLabels.klarForGjennomfoering;
      case PsPraksisaktivitetStatusDto.Gjennomfoert:
        return statusLabels.gjennomfoert;
      case PsPraksisaktivitetStatusDto.Avtalefeil:
        return statusLabels.avtalefeil;
    }
  };

  const findRole = (rolle: PraksisstedRollerDto | StudiestedRollerDto) => {
    switch (rolle) {
      case PraksisstedRollerDto.Koordinator:
      case StudiestedRollerDto.Koordinator:
        return formatMessage({ id: psaRoleTranslations.koordinator.id });
      case PraksisstedRollerDto.Veileder:
        return formatMessage({ id: psaRoleTranslations.veileder.id });
      case StudiestedRollerDto.Underviser:
        return formatMessage({ id: uhaRoleTranslations.underviser.id });
    }
    return '';
  };

  const headers = [
    formatMessage({ id: translations.activitiesHeaderPeriod.id }),
    formatMessage({ id: translations.activitiesHeaderType.id }),
    formatMessage({ id: translations.activitiesHeaderRole.id }),
    formatMessage({ id: translations.activitiesHeaderStatus.id }),
  ];

  const getRow = (
    aktivitet: PsPraksisaktivitetInfoDto | UhPraksisaktivitetInfoDto,
  ) => {
    let headerNum = 0;

    return (
      <TableRow key={aktivitet.id}>
        <TableCellTd header={headers[headerNum++]}>
          {`${dateWithLocale(
            new Date(aktivitet.startDato),
            locale as Language,
          )} - ${dateWithLocale(
            new Date(aktivitet.sluttDato),
            locale as Language,
          )}`}
        </TableCellTd>
        <TableCellTd header={headers[headerNum++]}>
          <Link to={`/calendar/activity/${aktivitet.id}/`}>
            {`${aktivitet.praksis.navn} ${
              aktivitet.krav ? `- ${aktivitet.krav}` : ''
            }`}
          </Link>
        </TableCellTd>
        <TableCellTd header={headers[headerNum++]}>
          {isPraksisstedBruker
            ? (aktivitet.rolle as PraksisstedRollerDto[])
                .map((rolle) => findRole(rolle))
                .join(' / ')
            : (aktivitet.rolle as StudiestedRollerDto[])
                .map((rolle) => findRole(rolle))
                .join(' / ')}
        </TableCellTd>
        <TableCellTd header={headers[headerNum]}>
          {aktivitet.status && findStatus(aktivitet.status)}
        </TableCellTd>
      </TableRow>
    );
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: PsPraksisaktivitetInfoDto | UhPraksisaktivitetInfoDto,
    b: PsPraksisaktivitetInfoDto | UhPraksisaktivitetInfoDto,
  ) => {
    switch (col) {
      case 0:
        return compare(
          Date.parse(a.startDato).toString(),
          Date.parse(b.startDato).toString(),
        );
      case 1:
        return compare(a.praksis.navn, b.praksis.navn);
      case 2:
        if (!a.rolle || !a.rolle[0] || !b.rolle || !b.rolle[0]) break;
        return compare(a.rolle[0], b.rolle[0]);
      case 3:
        if (!a.status || !b.status) break;
        return compare(findStatus(a.status), findStatus(b.status));
    }
    return 0;
  };

  const sortStudentsByActiveCol = (col: number, ascending: boolean) => {
    aktiviteterInStudyYear.sort((a, b) =>
      ascending ? sort(col, b, a) : sort(col, a, b),
    );

    setTableRows(aktiviteterInStudyYear.map(getRow));
  };

  const canSortIndices = [0, 1, 2, 3];

  const getHeaders = () =>
    headers.map((header, index) => (
      <TableCellTh
        key={header}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortStudentsByActiveCol}
        canSort={canSortIndices.includes(index)}
      >
        {header}
      </TableCellTh>
    ));

  const initSort = () => {
    if (praksisaktiviteter && !tableRows) {
      sortStudentsByActiveCol(activeCol, false);
    }
  };

  React.useEffect(initSort);

  return (
    <div className={css.activities}>
      <PanelHeader className={css.header} title={title} subtitle={subtitle} />
      {!loading && tableRows && tableRows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow top>{getHeaders()}</TableRow>
          </TableHeader>
          <TableMain>{tableRows}</TableMain>
        </Table>
      ) : (
        <>
          {!loading && (
            <EmptyState title={emptyStateTitle} text={emptyStateText} />
          )}
        </>
      )}
    </div>
  );
};

export default Activities;
